import { useContext, useMemo, useState, useEffect } from "react";

import {
	Avatar,
	Box,
	Button,
	Grid,
	IconButton,
	Step,
	StepConnector,
	stepConnectorClasses,
	StepLabel,
	Stepper,
	styled,
	Typography,
} from "@mui/material";

import InfoOutlineIcon from "@mui/icons-material/InfoOutline";

import { Context } from "../Context";
import { Step1 } from "./Step1";
import { Step2 } from "./Step2";
import { Step3 } from "./Step3";
import { Step4 } from "./Step4";

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
	[`&.${stepConnectorClasses.alternativeLabel}`]: {
		top: 28,
		padding: "0 16px",
	},
}));

const HorizontalStepper = () => {
	const {
		setViewActive,
		selectedReports,
		selectedEmployees,
		selectedDates,
		closing,
		executeReport,
	} = useContext(Context);

	const [activeStep, setActiveStep] = useState(0);

	const steps = useMemo(
		() => [
			{ label: "Seleccione el informe", stepComponent: <Step1 /> },
			{ label: "Selecciona Colaboradores", stepComponent: <Step2 /> },
			{ label: "Selecciona rango de fechas", stepComponent: <Step3 /> },
			{ label: "Resumen", stepComponent: <Step4 /> },
		],
		[]
	);

	const StepIconComponent = ({ position }) => {
		return (
			<Avatar
				sx={{
					bgcolor:
						activeStep === position - 1
							? "primary.main"
							: "transparent",
					color: "white",
					fontWeight: "bold",
					width: 56,
					height: 56,
					cursor: "pointer",
				}}
				onClick={() => setActiveStep(position - 1)}
			>
				{position}
			</Avatar>
		);
	};

	const disableNextButton = {
		0: () =>
			selectedReports.filter((item) => !item.startsWith("category-"))
				.length === 0,
		1: () => selectedEmployees.length === 0,
		2: () => selectedDates.length !== 2 || selectedDates.includes(null),
		3: () => false,
	};

	useEffect(() => {}, [selectedReports]);

	return (
		<>
			{activeStep === 4 ? (
				<>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column", // Apila los elementos verticalmente
							alignItems: "center", // Centra horizontalmente
							justifyContent: "center", // Centra verticalmente
							height: "80vh", // Ocupa la mayor parte de la pantalla
							mt: 0, // Elimina el margen superior
						}}
					>
						<IconButton size="large" color="info" disabled>
							<InfoOutlineIcon color="info" />
						</IconButton>

						<Typography
							variant="h6"
							color="textSecondary"
							sx={{ mt: 2 }}
						>
							Generando reporte
						</Typography>

						<Typography
							variant="body1"
							color="textSecondary"
							sx={{ mt: 2, textAlign: "center", px: 20 }}
						>
							Se esta generando el reporte. Este proceso puede
							tardar unos minutos. Una vez finalizado el proceso
							podrás verl el reporte en el{" "}
							<b>Historial de reportes</b>.
						</Typography>
					</Box>

					<Grid container spacing={2}>
						<Grid
							size={3}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Button
								color="inherit"
								variant="outlined"
								onClick={() => {
									setViewActive("table");
									closing();
									setActiveStep(0);
								}}
								sx={{
									minWidth: "100px",
								}}
							>
								Historial de reportes
							</Button>
						</Grid>

						<Grid size={6} />

						<Grid
							size={3}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Button
								color="primary"
								variant="contained"
								disableElevation
								onClick={() => {
									executeReport();
									closing();
									setActiveStep(0);
									setViewActive("stepper");
								}}
								sx={{
									minWidth: "100px",
								}}
							>
								+ Nuevo reporte
							</Button>
						</Grid>
					</Grid>
				</>
			) : (
				<>
					<Stepper
						activeStep={activeStep}
						alternativeLabel
						connector={<ColorlibConnector />}
						sx={{
							//bgcolor: "green",
							mt: 3,
						}}
					>
						{steps.map((step, index) => (
							<Step key={step.label}>
								<StepLabel
									StepIconComponent={() => (
										<StepIconComponent
											position={index + 1}
										/>
									)}
								>
									{step.label}
								</StepLabel>
							</Step>
						))}
					</Stepper>

					<Box
						sx={{
							p: 3,
							//bgcolor: "yellowgreen",
							height: "calc(100vh - 220px)",
						}}
					>
						<Grid container spacing={2}>
							<Grid
								size={12}
								sx={{
									height: "calc(100vh - 300px)",
									paddingX: "4vw",
								}}
							>
								{steps[activeStep].stepComponent}
							</Grid>
						</Grid>

						<Grid container spacing={2}>
							<Grid
								size={3}
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								{activeStep > 0 && (
									<Button
										color="inherit"
										variant="outlined"
										onClick={() =>
											setActiveStep((prev) => prev - 1)
										}
										disabled={activeStep === 0}
										sx={{
											minWidth: "100px",
										}}
									>
										Atras
									</Button>
								)}

								{activeStep === 0 && (
									<Button
										color="inherit"
										variant="outlined"
										onClick={() => setViewActive("table")}
										disabled={activeStep !== 0}
										sx={{
											minWidth: "100px",
										}}
									>
										Cancelar
									</Button>
								)}
							</Grid>

							<Grid size={6} />

							<Grid
								size={3}
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Button
									color="primary"
									variant="contained"
									disableElevation
									onClick={() =>
										setActiveStep((prev) => prev + 1)
									}
									disabled={disableNextButton[activeStep]?.()}
									sx={{
										minWidth: "100px",
									}}
								>
									Siguiente
								</Button>
							</Grid>
						</Grid>
					</Box>
				</>
			)}
		</>
	);
};

export { HorizontalStepper };
